import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { PractitionerStaff } from '@/services/practitionerStaffService';
import { Button } from '../ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { practitionerStaffService } from '@/services/practitionerStaffService';

interface ArchivePractitionerStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practitionerStaff: PractitionerStaff | null;
  onSuccess: () => void;
}

const ArchivePractitionerStaffDialog: React.FC<ArchivePractitionerStaffDialogProps> = ({
  open,
  onOpenChange,
  practitionerStaff,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { clinic } = useClinic();

  if (!practitionerStaff) return null;

  const isArchived = practitionerStaff.status === 'archived';
  const action = isArchived ? 'activate' : 'deactivate';
  const actionText = isArchived ? 'Activate' : 'Deactivate';
  const actionDescription = isArchived
    ? 'This will restore the practitioner staff and make them active again.'
    : 'This will archive the practitioner staff. They will no longer be able to access the system, but their data will be preserved.';

  const handleConfirm = async () => {
    try {
      setIsLoading(true);

      if (isArchived) {
        await practitionerStaffService.restoreClinicPractitionerStaff(clinic?.id, practitionerStaff.id);
        toast({
          title: 'Success',
          description: 'Practitioner staff has been activated successfully.',
          variant: 'success',
        });
      } else {
        await practitionerStaffService.archiveClinicPractitionerStaff(clinic?.id, practitionerStaff.id);
        toast({
          title: 'Success',
          description: 'Practitioner staff has been deactivated successfully.',
          variant: 'success',
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error(`Error ${action}ing practitioner staff:`, error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${action} practitioner staff. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isArchived ? 'bg-emerald-500/10' : 'bg-orange-500/10'
            }`}>
              {isArchived ? (
                <ArchiveRestore className="h-6 w-6 text-emerald-600" />
              ) : (
                <Archive className="h-6 w-6 text-orange-600" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {actionText} Practitioner Staff
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                Are you sure you want to {action} this staff?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-slate-600">
            Are you sure you want to {action} <span className="font-semibold text-slate-900">{practitionerStaff.full_name}</span>? {actionDescription}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={
              isArchived
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-orange-600 hover:bg-orange-700'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isArchived ? 'Activating...' : 'Deactivating...'}
              </>
            ) : (
              <>
                {isArchived ? (
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                ) : (
                  <Archive className="h-4 w-4 mr-2" />
                )}
                {actionText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchivePractitionerStaffDialog;
